import React, { useMemo, useState } from "react";
import { FruitifyContainer } from "../../../shared-components/Containers";
import SchoolSearchAutocomplete from "../../school/components/SchoolSearchAutocomplete";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Checkbox,
  Grid,
  Pagination,
  Stack,
  Typography,
} from "@mui/material";
import { Refresh, Visibility } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import SubscriptionStatus from "../components/SubscriptionStatus";
import { useApiClient } from "../../../api";
import { JobsAccordion } from "../../../shared-components/JobsAccordion";

import SchoolWorkflowEnrollmentDialog from "../onboarding/SchoolWorkflowEnrollmentDialog";

function SchoolSummary({
  school,
  schoolGroup = null,
  disableView = false,
  selected = false,
  onSelect = null,
}) {
  const navigate = useNavigate();
  const navRoute = schoolGroup
    ? `/schoolGroup/${schoolGroup.id}/schools/${school.id}`
    : `/superuser/schools/${school.id}`;
  return (
    <Card sx={{ display: "flex", flexDirection: "column", flexGrow: 1 }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Stack
          direction={"row"}
          alignItems={"center"}
          justifyContent={"space-between"}
          spacing={2}
        >
          {onSelect && (
            <Checkbox
              checked={selected}
              onChange={(event) => onSelect(school, event.target.checked)}
              inputProps={{ "aria-label": `Select ${school.name}` }}
            />
          )}
          <Typography variant={"subtitle1"}>{school.name} </Typography>
          <SubscriptionStatus
            status={school.subscriptionStatus}
            approved={school?.subscription?.isSchoolApproved}
          />
        </Stack>

        <Typography variant={"body2"}>{school.addressLine1}</Typography>
        <Typography variant={"body2"}>{school.town}</Typography>
        <Chip
          label={school.phaseOfEducationDisplay}
          size="small"
          variant="outlined"
          sx={{ mt: 1 }}
        />
      </CardContent>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2, mr: 2 }}>
        <Button
          startIcon={<Visibility />}
          variant="outlined"
          onClick={() => navigate(navRoute)}
          disabled={disableView}
        >
          View
        </Button>
      </Box>
    </Card>
  );
}

function SchoolsDashboard({ superuserView = false }) {
  // TODO: move this from superuser folder?
  const { groupId } = useParams();
  const ApiClient = useApiClient();
  const [selectedSchools, setSelectedSchools] = useState([]);
  const [enrollmentOpen, setEnrollmentOpen] = useState(false);
  const selectSchools = (schools) => {
    const next = [
      ...new Map(
        [...selectedSchools, ...schools].map((school) => [school.id, school]),
      ).values(),
    ];
    if (next.length > 100) {
      enqueueSnackbar(
        "Select at most 100 schools. Clear some selections before adding more.",
        { variant: "warning" },
      );
      return;
    }
    setSelectedSchools(next);
  };
  const selectSchool = (school, checked) => {
    if (checked) selectSchools([school]);
    else
      setSelectedSchools((current) =>
        current.filter((item) => item.id !== school.id),
      );
  };
  const [refreshJob, setRefreshJob] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [queuingRefresh, setQueuingRefresh] = useState(false);
  const refreshInProgress = useMemo(
    () =>
      ["pending", "in_progress", "in progress"].includes(
        refreshJob?.status?.toLowerCase(),
      ),
    [refreshJob],
  );

  const handleRefresh = async () => {
    setQueuingRefresh(true);
    try {
      const result = await ApiClient.wonde.refreshWondeSchools();
      if (!result.success) {
        enqueueSnackbar(result.message, { variant: "error" });
        return;
      }
      setRefreshJob(result.job);
      enqueueSnackbar(result.message, { variant: "success" });
    } catch (_error) {
      enqueueSnackbar("Unable to refresh schools from Wonde.", {
        variant: "error",
      });
    } finally {
      setQueuingRefresh(false);
    }
  };

  const handleJobUpdate = (job) => {
    setRefreshJob(job);
    if (["completed", "failed"].includes(job.status?.toLowerCase())) {
      setRefreshKey((value) => value + 1);
      enqueueSnackbar(
        job.status?.toLowerCase() === "completed"
          ? "Schools refreshed from Wonde."
          : job.message || "The Wonde school refresh failed.",
        {
          variant:
            job.status?.toLowerCase() === "completed" ? "success" : "error",
        },
      );
    }
  };

  if (!superuserView && !groupId) {
    return <></>;
  }
  const schoolGroup = groupId ? { id: groupId } : null;

  return (
    <FruitifyContainer>
      {superuserView && (
        <Stack spacing={2} sx={{ mb: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={handleRefresh}
              disabled={queuingRefresh || refreshInProgress}
            >
              Refresh schools from Wonde
            </Button>
          </Box>
          <JobsAccordion
            jobs={refreshJob ? [refreshJob] : []}
            onJobUpdate={handleJobUpdate}
          />
        </Stack>
      )}
      <SchoolSearchAutocomplete
        subscriptionSwitch
        defaultSubscribedOnly={superuserView}
        defaultApprovalFilter={superuserView ? "approved" : "all"}
        showResults
        schoolGroup={schoolGroup}
        phaseFilter={superuserView}
        refreshKey={refreshKey}
      >
        {({ options, page, pageInfo, handlePageChange }) => (
          <>
            {superuserView && (
              <Stack
                direction="row"
                spacing={1}
                useFlexGap
                sx={{ flexWrap: "wrap", mt: 2, alignItems: "center" }}
              >
                <Typography>
                  {selectedSchools.length} schools selected
                </Typography>
                <Button
                  onClick={() => selectSchools(options)}
                  disabled={!options.length}
                >
                  Select page
                </Button>
                <Button
                  onClick={() => setSelectedSchools([])}
                  disabled={!selectedSchools.length}
                >
                  Clear selection
                </Button>
                <Button
                  variant="contained"
                  disabled={!selectedSchools.length}
                  onClick={() => setEnrollmentOpen(true)}
                >
                  Enroll to workflow
                </Button>
              </Stack>
            )}
            <Grid container spacing={4} sx={{ mt: 2 }}>
              {options.map((school) => (
                <Grid
                  item
                  size={{ xs: 12, sm: 6, md: 3 }}
                  key={school.id}
                  sx={{ display: "flex" }}
                >
                  <SchoolSummary
                    school={school}
                    selected={selectedSchools.some(
                      (item) => item.id === school.id,
                    )}
                    onSelect={superuserView ? selectSchool : null}
                    schoolGroup={schoolGroup}
                    disableView={!school.subscriptionStatus && !superuserView}
                  />
                </Grid>
              ))}
            </Grid>
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <Pagination
                count={pageInfo.hasNextPage ? page + 1 : page}
                page={page}
                onChange={handlePageChange}
              />
            </Box>
          </>
        )}
      </SchoolSearchAutocomplete>
      {superuserView && enrollmentOpen && (
        <SchoolWorkflowEnrollmentDialog
          api={ApiClient.marketing}
          schools={selectedSchools}
          onClose={() => setEnrollmentOpen(false)}
        />
      )}
    </FruitifyContainer>
  );
}

export { SchoolsDashboard, SchoolSummary };
